/* eslint-disable max-statements */
import { LoadingOverlay, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { get } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { Declaration, DeclarationDocumentsEnum, EnvironmentCountries, EORIGIN_DATE_FORMAT } from '@e-origin/shared';

import { AsyncAutocomplete, DatePickerInput, EucdmCode, MultiLangCodes, NumberInput, TextInput } from '@components';
import { useDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField } from '@utils';
import { DeclarationDocumentSchema, FormDocumentsDeclarationType, FormDocumentsInputType } from './DeclarationDocumentFormSchema';

interface DeclarationDocumentFormFactoryProps {
  type: DeclarationDocumentsEnum;
  editDisabled: boolean;
  form: UseFormReturnType<any>;
  declarationType: FormDocumentsDeclarationType.HIGH_VALUES | FormDocumentsDeclarationType.LOW_VALUES;
  isHeader: boolean;
  schema: DeclarationDocumentSchema;
  declaration?: Declaration;
}

export const DeclarationDocumentFormFactory: React.FC<DeclarationDocumentFormFactoryProps> = ({
  type,
  editDisabled,
  form,
  declarationType,
  isHeader,
  schema,
  declaration,
}) => {
  const { data: declarant, isLoading } = useDeclarant();
  const { data: settings } = useSettings();

  const declarationGroup = declarationType === FormDocumentsDeclarationType.HIGH_VALUES ? 'H1' : undefined;
  const codelistSystems = useCodeListSystem({
    group: declarationGroup,
    isExport: declaration?.generalInfo?.isExport,
  });

  const [formSchema, setFormSchema] = useState({} as any);
  useEffect(() => {
    setFormSchema(schema[type]);
  }, [type]);

  const getEucdmPath = (fieldName: string): string => `documents.${type}.${fieldName}`;

  const getFieldDisabledState = (isAmendmentDisabled: boolean, amendmentDisabledCountries: EnvironmentCountries[]): boolean => {
    if (editDisabled) return true;

    if (isAmendmentDisabled) {
      return disableAmendmentField({ declaration, settingsCountry: settings?.country, countries: amendmentDisabledCountries });
    }

    return false;
  };

  const renderFormField = (path: string, idx: number) => {
    const {
      type: fieldType,
      placeholder,
      isAmendmentDisabled,
      amendmentDisabledCountries,
      'data-pw': dataPw,
      code: fieldCode,
      system: fieldSystem,
    } = formSchema.fields[path];

    const eucdmPath = getEucdmPath(path);
    const disabled = getFieldDisabledState(isAmendmentDisabled, amendmentDisabledCountries);

    let fieldComponent = null;

    switch (fieldType) {
      case FormDocumentsInputType.AUTOCOMPLETE:
        fieldComponent = (
          <AsyncAutocomplete
            data-pw={dataPw}
            key={`${type}-${idx}`}
            {...form.getInputProps(path)}
            label={placeholder}
            disabled={disabled}
            fetchOptions={(search) => formSchema.fields[path].search(search, declarant.language, isHeader)}
            placeholder={placeholder}
          />
        );
        break;

      case FormDocumentsInputType.MULTILANG: {
        const resolvedCode = Array.isArray(fieldCode) ? (isHeader ? fieldCode[0] : fieldCode[1]) : fieldCode;
        const resolvedSystem = fieldSystem || codelistSystems;

        fieldComponent = (
          <MultiLangCodes
            data-pw={dataPw}
            key={`${type}-${idx}`}
            {...form.getInputProps(path)}
            label={placeholder}
            disabled={disabled}
            code={resolvedCode}
            system={resolvedSystem}
            placeholder={placeholder}
          />
        );
        break;
      }

      case FormDocumentsInputType.INPUT:
        fieldComponent = <TextInput data-pw={dataPw} key={`${type}-${idx}`} {...form.getInputProps(path)} label={placeholder} disabled={disabled} />;
        break;

      case FormDocumentsInputType.NUMBER:
        fieldComponent = <NumberInput data-pw={dataPw} key={`${type}-${idx}`} {...form.getInputProps(path)} label={placeholder} disabled={disabled} />;
        break;

      case FormDocumentsInputType.DATE:
        fieldComponent = (
          <DatePickerInput
            data-pw={dataPw}
            key={`${type}-${idx}`}
            {...form.getInputProps(path)}
            label={placeholder}
            disabled={disabled}
            valueFormat={EORIGIN_DATE_FORMAT}
            value={get(form.values, path) ? moment(get(form.values, path), EORIGIN_DATE_FORMAT).toDate() : null}
            onChange={(value) => {
              form.setFieldValue(path, value ? moment(value).format(EORIGIN_DATE_FORMAT) : '');
            }}
            clearable
          />
        );
        break;

      default:
        return null;
    }

    return (
      <EucdmCode path={eucdmPath} key={`${type}-${idx}`}>
        {fieldComponent}
      </EucdmCode>
    );
  };

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <Stack gap="xs">
      {formSchema?.fields &&
        Object.keys(formSchema.fields)
          .filter(
            (path) =>
              (!formSchema.fields[path]?.show || formSchema.fields[path].show()) &&
              (formSchema.fields[path].declarationType === declarationType ||
                [undefined, FormDocumentsDeclarationType.ALL].includes(formSchema.fields[path].declarationType)),
          )
          .map((path, idx) => renderFormField(path, idx))}
    </Stack>
  );
};
